'use client';

import {
  Badge,
  IconButton,
  Spinner,
  Text,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BotIcon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  HandGrabIcon,
  Key01Icon,
  PlayIcon,
  SquareIcon,
  FlashIcon,
} from '@terra/ui-icons';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ABORTING_UI_ESCAPE_MS,
  isAbortUiLocked,
  isActiveSession,
  type CrawlerSession,
  type CrawlerSessionStats,
  type CrawlerSessionType,
} from '../../hooks/useCrawlerSessions';
import { CrawlerSessionCard } from './CrawlerSessionCard';

const EMPTY_SET = new Set<string>();
const CRAWLER_TYPE_INDICATOR: Record<
  CrawlerSessionType,
  {
    icon: ReactNode;
    color: string;
  }
> = {
  SHALLOW: { icon: <FlashIcon size={14} />, color: 'yellow.400' },
  AI: { icon: <BotIcon size={14} />, color: 'purple.400' },
  AUTHENTICATION: { icon: <Key01Icon size={14} />, color: 'orange.400' },
  MANUAL: { icon: <HandGrabIcon size={14} />, color: 'blue.400' },
  FEATURE_ANALYSIS: { icon: <BotIcon size={14} />, color: 'blue.400' },
  NAVIGATION_PROFILE: { icon: <BotIcon size={14} />, color: 'teal.400' },
};

function RunningCrawlerIndicator({
  icon,
  color,
}: {
  icon: ReactNode;
  color: string;
}) {
  return (
    <HStack gap="0.5" alignItems="center">
      <Box color={color}>{icon}</Box>
      <Box w="6px" h="6px" borderRadius="full" bg="green.400" flexShrink={0} />
    </HStack>
  );
}

const WAITING_TIMEOUT_MS = 30000;
const TOOLTIP_CREDENTIAL_DISABLED = 'Credential is disabled';
const TOOLTIP_STARTING = 'Starting crawler...';
const TOOLTIP_ABORTING = 'Aborting all sessions...';
const TOOLTIP_ABORT_ALL = 'Abort all sessions for this credential';
const TOOLTIP_RUN_CRAWLER = 'Run crawler for this credential';

interface CrawlerCredentialGroupProps {
  credentialId: string | null;
  credentialName: string;
  credentialRole?: string;
  isDisabled?: boolean;
  sessions: CrawlerSession[];
  statsBySession: Record<string, CrawlerSessionStats>;
  onTriggerCrawler: () => void;
  onAbortSession: (sessionId: string) => void;
  /** Abort all sessions for this credential (null = anonymous) */
  onAbortByCredential?: (credentialId: string | null) => void;
  isTriggering?: boolean;
  abortingSessionIds: Set<string>;
  abortingCredentialIds?: Set<string>;
  isAbortingAll?: boolean;
}

export function CrawlerCredentialGroup({
  credentialId,
  credentialName,
  credentialRole,
  isDisabled = false,
  sessions,
  statsBySession,
  onTriggerCrawler,
  onAbortSession,
  onAbortByCredential,
  isTriggering = false,
  abortingSessionIds,
  abortingCredentialIds = EMPTY_SET,
  isAbortingAll = false,
}: CrawlerCredentialGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isWaitingForSessions, setIsWaitingForSessions] = useState(false);
  const [abortingSinceMs, setAbortingSinceMs] = useState<number | null>(null);
  const [abortUiNowMs, setAbortUiNowMs] = useState(() => Date.now());
  const wasTriggering = useRef(false);
  const waitingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasRunningSessions = sessions.some(isActiveSession);
  const hasNonManualRunningSessions = sessions.some(
    (s) => isActiveSession(s) && s.crawlerType !== 'MANUAL',
  );

  useEffect(() => {
    return () => {
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    const triggerJustStarted = isTriggering && !wasTriggering.current;
    const triggerJustEnded =
      !isTriggering && wasTriggering.current && isWaitingForSessions;

    if (triggerJustStarted) {
      setIsWaitingForSessions(true);
      waitingTimeoutRef.current = setTimeout(
        () => setIsWaitingForSessions(false),
        WAITING_TIMEOUT_MS,
      );
    }

    wasTriggering.current = isTriggering;
    if (hasRunningSessions && isWaitingForSessions) {
      setIsWaitingForSessions(false);
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
    }

    if (triggerJustEnded && !hasRunningSessions) {
      setIsWaitingForSessions(false);
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
    }
  }, [isTriggering, hasRunningSessions, isWaitingForSessions]);
  const showStartLoading = isTriggering || isWaitingForSessions;
  const activeNonManualSessions = sessions.filter(
    (s) => isActiveSession(s) && s.crawlerType !== 'MANUAL',
  );
  const allActiveAborting =
    activeNonManualSessions.length > 0 &&
    activeNonManualSessions.every((s) => s.status === 'ABORTING');
  const credentialTrackingId = credentialId || 'anonymous';
  const isLocallyAborting =
    isAbortingAll || abortingCredentialIds.has(credentialTrackingId);
  const abortingActive = isLocallyAborting || allActiveAborting;

  useEffect(() => {
    if (abortingActive) {
      setAbortingSinceMs((prev) => prev ?? Date.now());
    } else {
      setAbortingSinceMs(null);
    }
  }, [abortingActive]);

  useEffect(() => {
    if (abortingSinceMs == null) {
      return;
    }

    const remainingMs = Math.max(
      0,
      abortingSinceMs + ABORTING_UI_ESCAPE_MS - Date.now(),
    );
    const timeout = setTimeout(() => {
      setAbortUiNowMs(Date.now());
    }, remainingMs);

    return () => clearTimeout(timeout);
  }, [abortingSinceMs]);

  const isAbortingCredential = isAbortUiLocked({
    isLocallyAborting,
    allActiveAborting,
    abortingSinceMs,
    nowMs: abortUiNowMs,
  });
  const runningCrawlerTypes = (
    Object.keys(CRAWLER_TYPE_INDICATOR) as Array<CrawlerSessionType>
  ).filter((type) =>
    sessions.some((s) => isActiveSession(s) && s.crawlerType === type),
  );
  const hasSessions = sessions.length > 0;
  const handleToggle = () => {
    if (hasSessions) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleTrigger = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTriggerCrawler();
  };

  const handleAbortAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAbortByCredential) {
      onAbortByCredential(credentialId);
    }
  };

  return (
    <Box
      w="full"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.muted"
    >
      <HStack
        justifyContent="space-between"
        alignItems="center"
        p="2.5"
        cursor={hasSessions ? 'pointer' : 'default'}
        _hover={hasSessions ? { bg: 'bg.subtle' } : undefined}
        borderBottomWidth={isExpanded && hasSessions ? '1px' : '0'}
        borderColor="border.muted"
        onClick={handleToggle}
      >
        <HStack gap="2" alignItems="center" flex="1" minW="0">
          {hasSessions ? (
            <Box flexShrink={0}>
              {isExpanded ? (
                <ArrowDown01Icon size={14} />
              ) : (
                <ArrowRight01Icon size={14} />
              )}
            </Box>
          ) : (
            <Box w="14px" flexShrink={0} />
          )}

          <Text fontSize="sm" fontWeight="medium" truncate>
            {credentialName}
          </Text>

          {credentialRole && (
            <Badge variant="fill" size="sm">
              {credentialRole}
            </Badge>
          )}
        </HStack>

        <HStack gap="2" alignItems="center">
          {runningCrawlerTypes.map((type) => (
            <RunningCrawlerIndicator
              key={type}
              icon={CRAWLER_TYPE_INDICATOR[type].icon}
              color={CRAWLER_TYPE_INDICATOR[type].color}
            />
          ))}

          {hasNonManualRunningSessions ? (
            <Tooltip
              title={
                isAbortingCredential ? TOOLTIP_ABORTING : TOOLTIP_ABORT_ALL
              }
            >
              <IconButton
                variant="ghost"
                size="xs"
                disabled={isAbortingCredential}
                onClick={handleAbortAll}
                aria-label="Abort all sessions"
              >
                {isAbortingCredential ? (
                  <Spinner size="xs" color="red.400" />
                ) : (
                  <SquareIcon size={14} />
                )}
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                isDisabled
                  ? TOOLTIP_CREDENTIAL_DISABLED
                  : showStartLoading
                    ? TOOLTIP_STARTING
                    : TOOLTIP_RUN_CRAWLER
              }
            >
              <IconButton
                variant="ghost"
                size="xs"
                disabled={isDisabled || showStartLoading}
                onClick={handleTrigger}
                aria-label="Run crawler"
              >
                {showStartLoading ? (
                  <Spinner size="xs" color="blue.400" />
                ) : (
                  <PlayIcon size={14} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      {hasSessions && isExpanded && (
        <VStack gap="0" py="1">
          {sessions.map((session) => (
            <CrawlerSessionCard
              key={session.id}
              session={session}
              stats={statsBySession[session.id]}
              onAbort={
                session.crawlerType !== 'MANUAL'
                  ? () => onAbortSession(session.id)
                  : undefined
              }
              isAborting={abortingSessionIds.has(session.id)}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
}
